/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AddExtraItemRequest } from '../models/AddExtraItemRequest';
import type { AddExtraItemsRequest } from '../models/AddExtraItemsRequest';
import type { AddOrderExtraPaymentRequest } from '../models/AddOrderExtraPaymentRequest';
import type { AmendOrderRequestData } from '../models/AmendOrderRequestData';
import type { AmendOrderResponse } from '../models/AmendOrderResponse';
import type { AssignOrderToAreaDTO } from '../models/AssignOrderToAreaDTO';
import type { AssignOrderToResourceDTO } from '../models/AssignOrderToResourceDTO';
import type { AvailableDatesWindowRequest } from '../models/AvailableDatesWindowRequest';
import type { CancelCheckinRequestData } from '../models/CancelCheckinRequestData';
import type { CheckinRequestData } from '../models/CheckinRequestData';
import type { CloseOrderBalanceRequest } from '../models/CloseOrderBalanceRequest';
import type { CommunicationTrackingEventOut } from '../models/CommunicationTrackingEventOut';
import type { CreateOrderRequestData } from '../models/CreateOrderRequestData';
import type { CreatePendingOrderRequestData } from '../models/CreatePendingOrderRequestData';
import type { CustomerByPhoneData } from '../models/CustomerByPhoneData';
import type { DateAvailabilityOut } from '../models/DateAvailabilityOut';
import type { DateAvailabilityOutMultipleOrderPages } from '../models/DateAvailabilityOutMultipleOrderPages';
import type { EditOrderRequestData } from '../models/EditOrderRequestData';
import type { ExtraPaymentResult } from '../models/ExtraPaymentResult';
import type { GetAvailableDatesRequestData } from '../models/GetAvailableDatesRequestData';
import type { GetAvailableDatesRequestDataMultipleOrderPages } from '../models/GetAvailableDatesRequestDataMultipleOrderPages';
import type { OpenBalancesPageData } from '../models/OpenBalancesPageData';
import type { OpenBalancesSummaryData } from '../models/OpenBalancesSummaryData';
import type { OpenBalancesTab } from '../models/OpenBalancesTab';
import type { OrderData } from '../models/OrderData';
import type { OrderSortBy } from '../models/OrderSortBy';
import type { OrderStatus } from '../models/OrderStatus';
import type { PhoneSuggestionData } from '../models/PhoneSuggestionData';
import type { ProjectSelectionPageData } from '../models/ProjectSelectionPageData';
import type { PublicCatalogCategoryData } from '../models/PublicCatalogCategoryData';
import type { RescheduleQuoteData } from '../models/RescheduleQuoteData';
import type { ResolvePendingOrderRequestData } from '../models/ResolvePendingOrderRequestData';
import type { SaveOrderRequestData } from '../models/SaveOrderRequestData';
import type { SubmitProjectSelectionRequest } from '../models/SubmitProjectSelectionRequest';
import type { UpdateExtraItemRequest } from '../models/UpdateExtraItemRequest';
import type { UpdateExtraPaymentRequest } from '../models/UpdateExtraPaymentRequest';
import type { UpdateOrderCommentRequest } from '../models/UpdateOrderCommentRequest';
import type { UpdatePendingOrderRequestData } from '../models/UpdatePendingOrderRequestData';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class OrderService {
  /**
   * Get Available Dates
   * @param requestBody
   * @returns DateAvailabilityOutMultipleOrderPages Successful Response
   * @throws ApiError
   */
  public static getAvailableDatesMultipleWorkshops(
    requestBody: GetAvailableDatesRequestDataMultipleOrderPages,
  ): CancelablePromise<Array<DateAvailabilityOutMultipleOrderPages>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/available_dates_multiple',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Available Dates Admin
   * @param requestBody
   * @returns DateAvailabilityOut Successful Response
   * @throws ApiError
   */
  public static getAvailableDatesAdmin(
    requestBody: GetAvailableDatesRequestData,
  ): CancelablePromise<Array<DateAvailabilityOut>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/available_dates/admin',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Save Order
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static saveOrder(requestBody: SaveOrderRequestData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/save_order',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Abandon Order
   * @param orderId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static abandonOrder(orderId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/abandon_order',
      query: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Edit Order
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static editOrder(requestBody: EditOrderRequestData): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/edit_order',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Order Extra Payment
   * @param requestBody
   * @returns ExtraPaymentResult Successful Response
   * @throws ApiError
   */
  public static addOrderExtraPayment(requestBody: AddOrderExtraPaymentRequest): CancelablePromise<ExtraPaymentResult> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/extra_payment',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Order Extra Payment
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static updateOrderExtraPayment(requestBody: UpdateExtraPaymentRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/order/extra_payment',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Remove Order Extra Payment
   * @param orderId
   * @param lineId
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static removeOrderExtraPayment(orderId: string, lineId: string): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/order/extra_payment',
      query: {
        order_id: orderId,
        line_id: lineId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Order Extra Items
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static addOrderExtraItems(requestBody: AddExtraItemsRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/extra_items/bulk',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Add Order Extra Item
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static addOrderExtraItem(requestBody: AddExtraItemRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/extra_items',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Order Extra Item
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static updateOrderExtraItem(requestBody: UpdateExtraItemRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/order/extra_items',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Remove Order Extra Item
   * @param orderId
   * @param itemLineId
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static removeOrderExtraItem(orderId: string, itemLineId: string): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/order/extra_items',
      query: {
        order_id: orderId,
        item_line_id: itemLineId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Close Order Balance
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static closeOrderBalance(requestBody: CloseOrderBalanceRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/close_balance',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Open Balances Summary
   * @returns OpenBalancesSummaryData Successful Response
   * @throws ApiError
   */
  public static getOpenBalancesSummary(): CancelablePromise<OpenBalancesSummaryData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/open_balances/summary',
    });
  }

  /**
   * Get Open Balances
   * @param tab Open bills or bills closed with a note
   * @param page Page number
   * @param perPage Items per page
   * @param search Search value
   * @param dateFrom Booking date from, inclusive (YYYY-MM-DD)
   * @returns OpenBalancesPageData Successful Response
   * @throws ApiError
   */
  public static getOpenBalances(
    tab: OpenBalancesTab,
    page: number = 1,
    perPage: number = 25,
    search?: string | null,
    dateFrom?: string | null,
  ): CancelablePromise<OpenBalancesPageData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/open_balances',
      query: {
        tab: tab,
        page: page,
        per_page: perPage,
        search: search,
        date_from: dateFrom,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Assign Order To Resource
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static assignOrderToResource(requestBody: AssignOrderToResourceDTO): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/assign-order-to-resource',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Assign Order To Area
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static assignOrderToArea(requestBody: AssignOrderToAreaDTO): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/assign-order-to-area',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Delete Order
   * @param orderId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static deleteOrder(orderId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/delete_order',
      query: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Event Orders
   * @param parentId
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static getEventOrders(parentId: string): CancelablePromise<Array<OrderData>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/get_event_orders',
      query: {
        parent_id: parentId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Checkin
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static checkinOrder(requestBody: CheckinRequestData): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/checkin',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Cancel Checkin
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static cancelCheckinOrder(requestBody: CancelCheckinRequestData): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/checkin/cancel',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Order
   * @param orderId
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static getOrder(orderId: string): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/get_order',
      query: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Order Email Tracking
   * @param orderId
   * @returns CommunicationTrackingEventOut Successful Response
   * @throws ApiError
   */
  public static getOrderCommunicationsTracking(
    orderId: string,
  ): CancelablePromise<Array<CommunicationTrackingEventOut>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/communications-tracking',
      query: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Fetch Order
   * @param orderId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static fetchOrderById(orderId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/id',
      query: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Fetch Order By Number
   * @param orderNumber
   * @param orderEmail
   * @returns any Successful Response
   * @throws ApiError
   */
  public static fetchOrderByNumber(orderNumber: any, orderEmail: any): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/number',
      query: {
        order_number: orderNumber,
        order_email: orderEmail,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Amend Order
   * @param requestBody
   * @returns AmendOrderResponse Successful Response
   * @throws ApiError
   */
  public static amendOrder(requestBody: AmendOrderRequestData): CancelablePromise<AmendOrderResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/amend_order',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Reschedule not allowed or target slot unavailable`,
        404: `Order not found`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Reschedule Quote
   * @param orderId
   * @returns RescheduleQuoteData Successful Response
   * @throws ApiError
   */
  public static getRescheduleQuote(orderId: string): CancelablePromise<RescheduleQuoteData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/reschedule_quote',
      query: {
        order_id: orderId,
      },
      errors: {
        404: `Order not found`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Project Selection Page
   * @param orderId
   * @returns ProjectSelectionPageData Successful Response
   * @throws ApiError
   */
  public static getProjectSelectionPage(orderId: string): CancelablePromise<ProjectSelectionPageData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/project_selection',
      query: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Submit Project Selection
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static submitProjectSelection(requestBody: SubmitProjectSelectionRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/order/project_selection',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Project Selection Catalog
   * @param accountId
   * @param orderPageId
   * @returns PublicCatalogCategoryData Successful Response
   * @throws ApiError
   */
  public static getProjectSelectionCatalog(
    accountId: string,
    orderPageId: string,
  ): CancelablePromise<Array<PublicCatalogCategoryData>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/project_selection/catalog',
      query: {
        account_id: accountId,
        order_page_id: orderPageId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Archived Orders
   * @param page Page number
   * @param perPage Items per page
   * @param search Search value
   * @param sortBy Sort field
   * @param orderPageIds Filter by order page IDs
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static getArchivedOrders(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    sortBy?: OrderSortBy,
    orderPageIds?: Array<string>,
  ): CancelablePromise<Array<OrderData>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/get_archived_orders',
      query: {
        page: page,
        per_page: perPage,
        search: search,
        sort_by: sortBy,
        order_page_ids: orderPageIds,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Notify Pickup
   * @param orderId
   * @returns any Successful Response
   * @throws ApiError
   */
  public static notifyPickup(orderId: string): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/notify-pickup',
      query: {
        order_id: orderId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Orders By Phone
   * @param phone Customer phone in any format
   * @returns CustomerByPhoneData Successful Response
   * @throws ApiError
   */
  public static getOrdersByPhone(phone: string): CancelablePromise<CustomerByPhoneData> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/by_phone',
      query: {
        phone: phone,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Phone Suggestions
   * @param phonePrefix Start of a customer phone, any format
   * @returns PhoneSuggestionData Successful Response
   * @throws ApiError
   */
  public static getPhoneSuggestions(phonePrefix: string): CancelablePromise<Array<PhoneSuggestionData>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order/phone_suggestions',
      query: {
        phone_prefix: phonePrefix,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Order Comment
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static updateOrderComment(requestBody: UpdateOrderCommentRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/order/comment',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Available Dates Window
   * @param requestBody
   * @returns DateAvailabilityOut Successful Response
   * @throws ApiError
   */
  public static getAvailableDatesWindow(
    requestBody: AvailableDatesWindowRequest,
  ): CancelablePromise<Array<DateAvailabilityOut>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/available_dates/window',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Available Dates
   * @param requestBody
   * @returns DateAvailabilityOut Successful Response
   * @throws ApiError
   */
  public static getAvailableDates(
    requestBody: GetAvailableDatesRequestData,
  ): CancelablePromise<Array<DateAvailabilityOut>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/available_dates',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Order
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static createOrder(requestBody: CreateOrderRequestData): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/create_order',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Unprocessable Entity`,
      },
    });
  }

  /**
   * Create Pending Order
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static createPendingOrder(requestBody: CreatePendingOrderRequestData): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/pending_order',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Unprocessable Entity`,
      },
    });
  }

  /**
   * Update Pending Order
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static updatePendingOrder(requestBody: UpdatePendingOrderRequestData): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/order/pending_order',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Resolve Pending Order
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static resolvePendingOrder(requestBody: ResolvePendingOrderRequestData): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/order/pending_order/resolve',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Upcoming Orders
   * @param page Page number
   * @param perPage Items per page
   * @param search Search value
   * @param statuses List of statuses
   * @param sortBy Sort field
   * @param orderPageIds Filter by order page IDs
   * @param dateFrom Booking date from, inclusive (YYYY-MM-DD)
   * @param dateTo Booking date to, inclusive (YYYY-MM-DD)
   * @param createdFrom Created at from, inclusive (ISO 8601, UTC if no offset)
   * @param createdTo Created at to, inclusive (ISO 8601, UTC if no offset)
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static getAllOrders(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    statuses?: Array<OrderStatus>,
    sortBy?: OrderSortBy,
    orderPageIds?: Array<string>,
    dateFrom?: string | null,
    dateTo?: string | null,
    createdFrom?: string | null,
    createdTo?: string | null,
  ): CancelablePromise<Array<OrderData>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/order',
      query: {
        page: page,
        per_page: perPage,
        search: search,
        statuses: statuses,
        sort_by: sortBy,
        order_page_ids: orderPageIds,
        date_from: dateFrom,
        date_to: dateTo,
        created_from: createdFrom,
        created_to: createdTo,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
